import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Tooltip,
  IconButton,
} from '@mui/material';
import crossIcon from 'packages/novous_web/src/assets/icons/CloseIcon.svg';
import ArrowLeftIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';
import ArrowRightIcon from 'packages/novous_web/src/assets/icons/next.svg';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
}

interface CommonAnalysisTableProps {
  title: string;
  headerText: string;
  columns: TableColumn[];
  data: any[];
  noDataMessage?: string;
  onClose?: () => void;
  onPaginationChange?: (cursor: any) => void;
  paginationInfo?: {
    lastEvaluatedKey?: any;
    hasMorePages?: boolean;
    totalRecords?: number;
    currentPage?: number;
    pageSize?: number;
  };
}

const CommonAnalysisTable: React.FC<CommonAnalysisTableProps> = ({
  title,
  headerText,
  columns,
  data,
  noDataMessage = 'No data available',
  onClose,
  onPaginationChange,
  paginationInfo,
}) => {
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [pagesData, setPagesData] = useState<any[][]>([]);
  const [nextCursor, setNextCursor] = useState<any>(null);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [cumulativeCount, setCumulativeCount] = useState(0);
  const [lastFetchedPage, setLastFetchedPage] = useState<number | null>(null);

  // Update pages data when new data is provided
  useEffect(() => {
    if (data && data.length > 0) {
      setPagesData((prevPages) => {
        const updatedPages = [...prevPages];
        updatedPages[currentPage - 1] = data;
        return updatedPages;
      });
      setLastFetchedPage(currentPage);

      // Update cumulative count - count all rows from page 1 up to current page
      let cumCount = 0;
      for (let i = 0; i < currentPage; i++) {
        if (i === currentPage - 1) {
          // Current page
          cumCount += data.length;
        } else if (pagesData[i]) {
          // Previous pages
          cumCount += pagesData[i].length;
        }
      }
      setCumulativeCount(cumCount);
    }
  }, [data]);

  // Update cumulative count when current page changes
  useEffect(() => {
    let cumCount = 0;
    for (let i = 0; i < currentPage; i++) {
      if (i === currentPage - 1) {
        // Current page
        const currentPageData = pagesData[i];
        if (currentPageData) {
          cumCount += currentPageData.length;
        }
      } else if (pagesData[i]) {
        // Previous pages
        cumCount += pagesData[i].length;
      }
    }
    setCumulativeCount(cumCount);
  }, [currentPage, pagesData]);

  // Update pagination info when it changes
  useEffect(() => {
    if (paginationInfo) {
      setNextCursor(paginationInfo.lastEvaluatedKey || null);
      setHasMorePages(paginationInfo.hasMorePages ?? false);
    }
  }, [paginationInfo]);

  const tableColumns = useMemo(
    () =>
      columns.map((col) => ({
        accessorKey: col.key,
        header: col.label,
        align: col.align || 'left',
      })),
    [columns],
  );

  const currentPageData: any[] = pagesData[currentPage - 1] ?? [];
  const displayData = currentPageData.length > 0 ? currentPageData : data;

  const totalPages = hasMorePages ? currentPage + 1 : currentPage;

  const handlePageChange = (page: number) => {
    console.log('Page change requested:', {
      currentPage,
      newPage: page,
      hasDataForPage: !!pagesData[page - 1],
      hasMorePages,
      nextCursor,
    });

    // Check if we already have data for this page (going backward)
    if (pagesData[page - 1]) {
      // Data already exists, just change page
      console.log('Data exists for page', page, 'switching to it');
      setCurrentPage(page);
    } else if (page > currentPage && onPaginationChange && hasMorePages) {
      // Need to fetch new page data (only if there are more pages)
      // Check if this is page-based or cursor-based pagination
      if (paginationInfo?.currentPage !== undefined && paginationInfo?.pageSize !== undefined) {
        // Page-based pagination (heatmap table) - pass page number
        console.log('Page-based pagination: Requesting page:', page);
        setCurrentPage(page);
        onPaginationChange(page);
      } else {
        // Cursor-based pagination (API tables) - pass cursor
        console.log('Cursor-based pagination: Fetching new page data with cursor:', nextCursor);
        setCurrentPage(page);
        onPaginationChange(nextCursor);
      }
    } else if (page < currentPage) {
      // Going backward - data should already be cached
      console.log('Going backward to page', page);
      setCurrentPage(page);
    } else {
      // Fallback
      setCurrentPage(page);
    }
  };

  return (
    <Box className="w-full h-full flex flex-col gap-2 items-start bg-white" sx={{ overflow: 'hidden' }}>
      {/* Header - Fixed */}
      <Box
        className="w-full"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <Box className="w-full" display="flex" alignItems="center" gap={1}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '36px',
              letterSpacing: '0%',
              color: '#1B1B1B',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <IconButton size="small" sx={{ p: 0 }} onClick={onClose}>
            <img src={crossIcon} alt="close icon" width={24} height={24} />
          </IconButton>
        </Box>
      </Box>

      {/* Header and Scrollable table container */}
      <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Fixed Header Table */}
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            flexShrink: 0,
          }}
        >
          <TableHead
            sx={{
              backgroundColor: '#F6F6FD',
              border: '1px solid #F0EFF8',
              borderRadius: '8px',
              height: '48px',
            }}
          >
            <TableRow>
              {tableColumns.map((column, index) => (
                <TableCell
                  key={column.accessorKey}
                  align={column.align as 'left' | 'center' | 'right'}
                  sx={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '0px',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                    height: '48px !important',
                    minHeight: '48px !important',
                    maxHeight: '48px !important',
                    padding: '0px 12px !important',
                    verticalAlign: 'middle',
                    '&:first-of-type': {
                      borderRadius: '8px 0 0 8px',
                    },
                    '&:last-of-type': {
                      borderRadius: '0 8px 8px 0',
                    },
                  }}
                >
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>

        {/* Scrollable Body Container */}
        <div className="table-wrapper" style={{ overflow: 'auto', width: '100%', flex: 1, minHeight: 0 }}>
          <TableContainer
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflow: 'visible !important',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '3px',
              },
            }}
          >
            <Table
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableRow-root': {
                  height: '48px !important',
                  minHeight: '48px !important',
                  maxHeight: '48px !important',
                },
                '& .MuiTableCell-root': {
                  height: '48px !important',
                  minHeight: '48px !important',
                  maxHeight: '48px !important',
                  padding: '0px 12px !important',
                  verticalAlign: 'middle !important',
                  boxSizing: 'border-box !important',
                  lineHeight: '1 !important',
                },
                '& .MuiTableBody-root .MuiTableRow-root': {
                  height: '48px !important',
                  minHeight: '48px !important',
                  maxHeight: '48px !important',
                },
                '& .MuiTableBody-root .MuiTableCell-root': {
                  height: '48px !important',
                  minHeight: '48px !important',
                  maxHeight: '48px !important',
                },
              }}
            >
              <TableBody>
                {displayData && displayData.length > 0 ? (
                  displayData.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        height: '48px !important',
                        minHeight: '48px !important',
                        maxHeight: '48px !important',
                        '& .MuiTableCell-root': {
                          height: '48px !important',
                          minHeight: '48px !important',
                          maxHeight: '48px !important',
                          verticalAlign: 'middle',
                          padding: '0px 12px !important',
                        },
                        backgroundColor:
                          rowIndex % 2 === 0
                            ? theme.palette.mode === 'dark'
                              ? '#121212'
                              : '#F0EFF8'
                            : 'inherit',
                        '&:hover': {
                          backgroundColor: rowIndex % 2 === 0 ? '#E8E5F9' : '#F5F5F5',
                        },
                      }}
                    >
                      {tableColumns.map((column) => (
                        <TableCell
                          key={`${rowIndex}-${column.accessorKey}`}
                          align={column.align as 'left' | 'center' | 'right'}
                          sx={{
                            height: '48px !important',
                            minHeight: '48px !important',
                            maxHeight: '48px !important',
                            padding: '0px 12px !important',
                            verticalAlign: 'middle',
                            lineHeight: '1 !important',
                            boxSizing: 'border-box !important',
                            fontSize: '14px',
                            color: '#666666',
                            fontWeight: 400,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 400 }}>
                            {row[column.accessorKey] !== undefined && row[column.accessorKey] !== null
                              ? row[column.accessorKey]
                              : '-'}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns.length}
                      align="center"
                      sx={{
                        padding: '48px 16px',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          minHeight: '300px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Box>

      {/* Pagination - Fixed */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width={'100%'}
        gap={1}
        mt={2}
        sx={{ flexShrink: 0 }}
      >
        <IconButton
          size="small"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            '&:disabled': {
              opacity: 0.5,
              borderColor: '#ccc',
            },
          }}
        >
          <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
        </IconButton>

        {/* Page numbers - sliding window of 3 */}
        {(() => {
          const visiblePages: number[] = [];
          const windowSize = 3;

          // Calculate the start of the window
          let startPage = Math.max(1, currentPage - 1);

          // Adjust if we're near the end
          if (currentPage === totalPages && totalPages > 1) {
            startPage = Math.max(1, totalPages - windowSize + 1);
          }

          // Generate visible page numbers
          for (let i = 0; i < windowSize && startPage + i <= totalPages; i++) {
            visiblePages.push(startPage + i);
          }

          return visiblePages.map((pageNum) => (
            <Box
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              sx={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: pageNum === currentPage ? theme.palette.primary.main : 'transparent',
                color: pageNum === currentPage ? '#FFFFFF' : '#808080',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: pageNum === currentPage ? theme.palette.primary.main : '#F5F5F5',
                },
              }}
            >
              {pageNum}
            </Box>
          ));
        })()}

        <IconButton
          size="small"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasMorePages}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            '&:disabled': {
              opacity: 0.5,
              borderColor: '#ccc',
            },
          }}
        >
          <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CommonAnalysisTable;
